import { Link, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import i18n from 'i18next'
import { toast } from 'sonner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  useGetUserSession,
  useSignUp,
  type SignUpPayload,
} from '@/shared/hooks/api/auth'
import { useUpdateLanguage } from '@/shared/hooks/api/user'
import { SUPPORTED_LANGUAGES } from '@/shared/config/languages'
import { AuthLayout } from '../auth-layout'
import { SignUpForm } from './components/sign-up-form'
import { WeChatAuthErrorAlert } from '../wechat/WeChatAuthErrorAlert'
import { WeChatQrLoginButton } from '../wechat/WeChatQrLoginButton'
import { isWeChatAuthEnabled } from '../wechat/isWeChatAuthEnabled'

function isSupportedInterfaceLanguage(code: string): boolean {
  return SUPPORTED_LANGUAGES.some((lang) => lang.value === code)
}

export function SignUp() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirect') ?? '/'
  const wechatError = searchParams.get('wechatError')
  const showWeChatAuth = isWeChatAuthEnabled()
  const { refetch: getSession } = useGetUserSession()
  const { mutateAsync: signUp, isPending: isSigningUp } = useSignUp()
  const { mutateAsync: updateLanguage } = useUpdateLanguage()

  async function handleSignUp(payload: SignUpPayload) {
    try {
      await signUp(payload)

      const chosenLanguage = i18n.language
      if (isSupportedInterfaceLanguage(chosenLanguage)) {
        try {
          await updateLanguage(chosenLanguage)
        } catch {
          // User is signed up; language can be changed later in settings.
        }
      }

      await getSession()
      navigate(redirectTo, { replace: true })
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        toast.error(t('signUp.errors.emailExists'))
        return
      }
      toast.error(t('signUp.errors.signUpFailed'))
    }
  }

  return (
    <AuthLayout>
      <Card className='gap-4'>
        <CardHeader>
          <CardTitle className='text-lg tracking-tight'>
            {t('signUp.title')}
          </CardTitle>
          <CardDescription>
            {t('signUp.alreadyHaveAccount')}{' '}
            <Link
              to='/sign-in'
              className='underline underline-offset-4 hover:text-primary'
            >
              {t('signUp.signInLink')}
            </Link>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <WeChatAuthErrorAlert errorCode={wechatError} translate={t} />
          {showWeChatAuth ? (
            <WeChatQrLoginButton label={t('wechat.continueWithWeChat')} />
          ) : null}
          {showWeChatAuth ? (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  {t('wechat.orContinueWithEmail')}
                </span>
              </div>
            </div>
          ) : null}
          <SignUpForm onSignUp={handleSignUp} isSigningUp={isSigningUp} />
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
